"use client";

import {
  createContext,
  use,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

export type GradePrice = {
  key: string;
  label: string;
  avgPriceCents: number;
  typicalLowCents?: number;
  typicalHighCents?: number;
};

type GradeSelection = {
  grades: readonly GradePrice[];
  selected: GradePrice | null;
  select: (key: string | null) => void;
};

export const GRADE_HASH_KEY = "grade";

function subscribeToHash(onChange: () => void): () => void {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
}

function readRequestedGrade(): string | null {
  return new URLSearchParams(window.location.hash.slice(1)).get(GRADE_HASH_KEY);
}

const GradeSelectionContext = createContext<GradeSelection | null>(null);

export function GradeSelectionProvider({
  grades,
  children,
}: {
  grades: readonly GradePrice[];
  children: ReactNode;
}) {
  const requestedKey = useSyncExternalStore(
    subscribeToHash,
    readRequestedGrade,
    () => null,
  );
  const [chosenKey, setChosenKey] = useState<string | null | undefined>(
    undefined,
  );
  const selectedKey = chosenKey === undefined ? requestedKey : chosenKey;
  const selected = grades.find((grade) => grade.key === selectedKey) ?? null;
  return (
    <GradeSelectionContext
      value={{ grades, selected, select: setChosenKey }}
    >
      {children}
    </GradeSelectionContext>
  );
}

export function useGradeSelection(): GradeSelection {
  const selection = use(GradeSelectionContext);
  if (!selection) {
    throw new Error("useGradeSelection needs a GradeSelectionProvider");
  }
  return selection;
}
